import { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { convertDraftToQuote } from '../utils/quoteItemBuilder';

// Create context
const QuoteContext = createContext();
const API_URL = process.env.REACT_APP_API_URL || '';

// Create provider component
export const QuoteProvider = ({ children }) => {
  // State for submitted quotes
  const [quotes, setQuotes] = useState([]);
  const [quotesTotal, setQuotesTotal] = useState(0);
  const [quotesHasMore, setQuotesHasMore] = useState(false);
  const [quotesOffset, setQuotesOffset] = useState(0);
  const [quotesLastParams, setQuotesLastParams] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // NEW: State for drafts
  const [drafts, setDrafts] = useState([]);
  const [draftsTotal, setDraftsTotal] = useState(0);
  const [draftsHasMore, setDraftsHasMore] = useState(false);
  const [draftsOffset, setDraftsOffset] = useState(0);
  const [draftsLastParams, setDraftsLastParams] = useState({});
  const [draftsLoading, setDraftsLoading] = useState(true);
  const [draftsError, setDraftsError] = useState(null);
  
  // State for current quote being built
  const [currentQuote, setCurrentQuote] = useState({
    items: [],
    totalPrice: 0
  });

  // State for quote loading feature
  const [quoteToLoad, setQuoteToLoad] = useState(null);
  const [targetCalculator, setTargetCalculator] = useState(null);
  // Track when a calculator session is editing an existing draft
  const [editingDraftId, setEditingDraftId] = useState(null);
  // Track a quote that should open the upload modal after navigation
  const [pendingUploadQuoteId, setPendingUploadQuoteId] = useState(null);

  // Track pending PrintLogic submissions to prevent duplicate calls
  const pendingSubmissions = useRef(new Set());

  // Get current user from auth context
  const { currentUser, authFetch } = useAuth();

  // Helper: derive effective price (override > calculated > priceBeforeOverride > price)
  const getEffectivePrice = useCallback((item) => {
    if (!item || typeof item !== 'object') return 0;
    const pick = (val) => val !== undefined && val !== null && !Number.isNaN(parseFloat(val));
    if (pick(item.overridePrice)) return parseFloat(item.overridePrice);
    if (pick(item.calculatedPrice)) return parseFloat(item.calculatedPrice);
    if (pick(item.priceBeforeOverride)) return parseFloat(item.priceBeforeOverride);
    return parseFloat(item.price) || 0;
  }, []);

  const computeItemsTotal = useCallback((items = []) => {
    if (!Array.isArray(items)) return 0;
    return items.reduce((sum, item) => sum + getEffectivePrice(item), 0);
  }, [getEffectivePrice]);

  // Generate a temporary user ID
  const [tempUserId] = useState(() => {
    const storedId = localStorage.getItem('temp_user_id');
    if (storedId) return storedId;
    
    const newId = 'temp_' + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('temp_user_id', newId);
    return newId;
  });

  // Helper function for quote type detection
  const getQuoteType = (quote) => {
    // FIRST: Check top-level productType and calculatorType (these are authoritative indicators)
    // Prioritize 'calculatorType' or 'productType' if available at the top level
    // This handles drafts saved directly from calculator state
    // BACKWARD COMPATIBILITY: Check both hyphen (old draft format) and underscore (new quote format) versions
    if (quote?.calculatorType === 'small-format' ||
        quote?.productType === 'small-format' ||
        quote?.productType === 'small_format') {
      return 'Small Format';
    }
    if (quote?.calculatorType === 'large-format' ||
        quote?.productType === 'large-format' ||
        quote?.productType === 'large_format') {
      return 'Large Format';
    }

    // FALLBACK: Check items array if top-level indicators not found
    const firstItem = quote?.items?.[0];
    if (!firstItem) {
      if (quote?.material) return 'Large Format';
      if (quote?.paperStock) return 'Small Format';
      return 'Unknown';
    }
    // Check productType FIRST (explicit format indicator) before checking other properties
    // This ensures NCR jobs (which have 'material' field for paper names) are correctly identified as Small Format
    if (firstItem.productType === 'small-format' || firstItem.productType === 'small_format') {
        return 'Small Format';
    }
    if (firstItem.productType === 'large-format' || firstItem.productType === 'large_format') {
        return 'Large Format';
    }
    // Fallback: Check other properties if productType not explicitly set (backward compatibility)
    if (firstItem.paperStock) {
        return 'Small Format';
    }
    if (firstItem.material) {
        return 'Large Format';
    }
    return 'Unknown';
  };

  // Function to prepare quote for editing
  const prepareQuoteForEditing = (quote) => {
    console.log('Preparing quote for editing:', quote);

    const quoteType = getQuoteType(quote);
    const calculator = quoteType === 'Small Format' ? 'small-format' : 'large-format';

    setQuoteToLoad(quote);
    setTargetCalculator(calculator);

    console.log(`Quote prepared for ${calculator} calculator`);

    // Return the target calculator so the UI can respond
    return calculator;
  };

  // Request opening the upload modal for a specific quote
  const requestUploadForQuote = (quoteId) => {
    if (!quoteId) return;
    setPendingUploadQuoteId(quoteId);
  };

  // Clear any queued upload modal requests once handled
  const clearPendingUploadRequest = () => setPendingUploadQuoteId(null);

  // Helper: Fetch reference data for draft conversion
  // Use a ref to cache the data across renders without causing re-renders
  const referenceDataCacheRef = useRef(null);

  const fetchReferenceData = async () => {
    if (referenceDataCacheRef.current) {
      return referenceDataCacheRef.current;
    }

    try {
      console.log('Fetching reference data for draft conversion...');

      // Fetch small-format reference data
      const [jobTypesRes, paperStocksRes, finishingProcessesRes, finishingMaterialsRes, printersRes] = await Promise.all([
        fetch(`${API_URL}/api/small-format/job-types`, { credentials: 'include' }),
        fetch(`${API_URL}/api/small-format/paper-stocks`),
        fetch(`${API_URL}/api/small-format/finishing-processes`),
        fetch(`${API_URL}/api/small-format/finishing-materials`),
        fetch(`${API_URL}/api/small-format/printers`)
      ]);

      const jobTypes = await jobTypesRes.json();
      const paperStocks = await paperStocksRes.json();
      const finishingProcesses = await finishingProcessesRes.json();
      const finishingMaterialsData = await finishingMaterialsRes.json();
      const printers = await printersRes.json();

      // Group finishing materials by process ID
      const finishingMaterials = finishingMaterialsData.reduce((acc, material) => {
        const processId = material.finish_process_id;
        if (!acc[processId]) {
          acc[processId] = [];
        }
        acc[processId].push(material);
        return acc;
      }, {});

      // Fetch large-format reference data
      const [
        vinylRes, boardRes, bannerRes, posterRes, laminateRes,
        rollerBannerRes, colouredVinylRes, machinesRes, lfJobTypesRes
      ] = await Promise.all([
        fetch(`${API_URL}/api/materials/vinyl`),
        fetch(`${API_URL}/api/materials/board`),
        fetch(`${API_URL}/api/materials/banner`),
        fetch(`${API_URL}/api/materials/poster`),
        fetch(`${API_URL}/api/materials/laminate`),
        fetch(`${API_URL}/api/materials/rollerbanner`),
        fetch(`${API_URL}/api/materials/colouredvinyl`),
        fetch(`${API_URL}/api/machines`),
        fetch(`${API_URL}/api/job-types`)
      ]);

      const vinylMaterials = await vinylRes.json();
      const boardMaterials = await boardRes.json();
      const bannerMaterials = await bannerRes.json();
      const posterMaterials = await posterRes.json();
      const laminateMaterials = await laminateRes.json();
      const rollerBannerMaterials = await rollerBannerRes.json();
      const colouredVinylMaterials = await colouredVinylRes.json();
      const printMachines = await machinesRes.json();
      const lfJobTypes = await lfJobTypesRes.json();

      referenceDataCacheRef.current = {
        paperStocks,
        finishingProcesses,
        finishingMaterials,
        jobTypes: [...jobTypes, ...lfJobTypes], // Merge small and large format job types
        printMachines,
        materials: {
          vinylMaterials,
          boardMaterials,
          bannerMaterials,
          posterMaterials,
          laminateMaterials,
          rollerBannerMaterials,
          colouredVinylMaterials
        }
      };

      console.log('Reference data fetched and cached successfully');
      return referenceDataCacheRef.current;
    } catch (error) {
      console.error('Error fetching reference data:', error);
      // Return minimal fallback data
      return {
        paperStocks: [],
        finishingProcesses: [],
        finishingMaterials: {},
        jobTypes: [],
        printMachines: [],
        materials: {}
      };
    }
  };

  // Function to prepare draft for quote review (without navigation)
  // Now uses the shared conversion utility for full schema compatibility
  const prepareDraftForQuoteReview = async (draft, referenceData = null) => {
    console.log('Preparing draft for quote review:', draft);

    try {
      // Fetch reference data if not provided
      const refData = referenceData || await fetchReferenceData();

      // Use the shared conversion utility
      const quoteData = convertDraftToQuote(draft, refData);

      // Load the data into currentQuote for the quote review modal
      setCurrentQuote(quoteData);

      console.log('Draft converted to quote using shared utility:', quoteData);
      return quoteData;
    } catch (error) {
      console.error('Error converting draft to quote:', error);

      // Fallback to basic conversion if utility fails
      const fallbackQuoteData = {
        items: [{
          id: `item_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
          jobName: draft.draftName || draft.jobName || 'Converted from Draft',
          productType: draft.calculatorType || draft.productType,
          price: draft.price || draft.totalPrice || 0,
          quantity: draft.quantity || 1,
          notes: draft.notes || '',
          breakdown: draft.breakdown || {}
        }],
        totalPrice: draft.totalPrice || draft.price || 0
      };

      setCurrentQuote(fallbackQuoteData);
      return fallbackQuoteData;
    }
  };

  // Function to clear loaded quote
  const clearLoadedQuote = () => {
    console.log('Clearing loaded quote');
    setQuoteToLoad(null);
    setTargetCalculator(null);
  };

  // Begin editing an existing draft by ID
  const startEditingDraft = (draftId) => {
    try {
      if (draftId && typeof draftId === 'string') {
        setEditingDraftId(draftId);
      }
    } catch (e) {
      console.error('Failed to start editing draft:', e);
    }
  };

  // Stop editing any draft (future saves will create new drafts)
  const stopEditingDraft = () => {
    setEditingDraftId(null);
  };
  // PrintLogic status sync function
const syncPrintlogicStatus = async () => {
  try {
    console.log('=== FRONTEND SYNC DEBUG: Starting ===');
    
    const response = await fetch(`${API_URL}/api/quotes/sync-printlogic-status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });
    
    const result = await response.json();
    console.log('=== FRONTEND SYNC DEBUG: API Response ===', result);
    
    if (result.success && result.quotes) {
      // Look specifically for order 122
      const order122Quote = result.quotes.find(q => q.printlogic_order_number === "122");
      if (order122Quote) {
        console.log('=== ORDER 122 FRONTEND DEBUG ===', {
          quoteName: order122Quote.quoteName,
          status: order122Quote.status,
          printlogic_order_status: order122Quote.printlogic_order_status,
          printlogic_last_synced: order122Quote.printlogic_last_synced
        });
      } else {
        console.log('=== ORDER 122 NOT FOUND in frontend response ===');
      }
      
      setQuotes(result.quotes);
      
      if (result.synced_count > 0) {
        console.log(`Successfully synced ${result.synced_count} order statuses`);
      }
      
      return result;
    }
    
    return null;
    
  } catch (error) {
    console.error('Error syncing PrintLogic statuses:', error);
    return null;
  }
};

// Manual sync function that can be called from UI
const manualSyncPrintlogicStatus = async () => {
  setIsLoading(true);
  try {
    const result = await syncPrintlogicStatus();
    if (result && result.success) {
      console.log('Manual status sync completed successfully');
      return true;
    } else {
      console.error('Manual status sync failed');
      return false;
    }
  } catch (error) {
    console.error('Error in manual status sync:', error);
    return false;
  } finally {
    setIsLoading(false);
  }
};
  // Function to fetch quotes with context parameter
  const buildPagedQuery = (params = {}) => {
    const query = new URLSearchParams({ paged: 'true' });
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.set(key, value);
      }
    });
    return query.toString();
  };

  const fetchQuotes = useCallback(async (context = 'management', options = {}) => {
    setIsLoading(true);
    try {
      const limit = options.limit || 50;
      const offset = options.offset || 0;
      const queryParam = buildPagedQuery({
        context: context !== 'management' ? context : undefined,
        limit,
        offset,
        search: options.search,
        status: options.status,
        source: options.source,
        format: options.format,
        temp_user_id: !currentUser ? tempUserId : undefined,
      });
      const response = currentUser
        ? await authFetch(`${API_URL}/api/quotes?${queryParam}`)
        : await fetch(`${API_URL}/api/quotes?${queryParam}`, { credentials: 'include' });
      
      const text = await response.text();
      
      try {
        const payload = JSON.parse(text);
        const items = Array.isArray(payload) ? payload : payload.items || [];
        setQuotes(options.append ? (prev) => [...prev, ...items] : items);
        setQuotesTotal(Array.isArray(payload) ? items.length : payload.total || items.length);
        setQuotesHasMore(!Array.isArray(payload) && Boolean(payload.hasMore));
        setQuotesOffset(offset + items.length);
        setQuotesLastParams({ context, ...options, limit, offset: offset + items.length });
        setError(null);
        
        // AUTO-SYNC: Check for quotes that might need status updates
        if (currentUser && items.length > 0) {
          const quotesWithPrintlogicOrders = items.filter(quote => 
            quote.printlogic_order_number && 
            quote.status && 
            !quote.status.toLowerCase().includes('completed')
          );
          
          if (quotesWithPrintlogicOrders.length > 0) {
            console.log(`Found ${quotesWithPrintlogicOrders.length} quotes that may need status sync`);
            
            // Sync statuses in the background
            setTimeout(async () => {
              await syncPrintlogicStatus();
            }, 500); // Small delay to let UI update first
          }
        }
        
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        setError('Invalid response from server');
      }
    } catch (err) {
      console.error('Error fetching quotes:', err);
      setError('Failed to load quotes. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, authFetch, tempUserId]);

  const loadMoreQuotes = useCallback(() => {
    if (!quotesHasMore || isLoading) return;
    const { context = 'management', ...params } = quotesLastParams;
    return fetchQuotes(context, { ...params, offset: quotesOffset, append: true });
  }, [fetchQuotes, quotesHasMore, isLoading, quotesLastParams, quotesOffset]);

  // Load quotes from API on component mount
  useEffect(() => {
    if (currentUser || tempUserId) {
      fetchQuotes();
      const refreshInterval = setInterval(fetchQuotes, 300000);
      return () => clearInterval(refreshInterval);
    }
  }, [currentUser, tempUserId, fetchQuotes]);


  // Function to fetch drafts with context parameter
  const fetchDrafts = useCallback(async (context = 'management', options = {}) => {
    setDraftsLoading(true);
    try {
      const limit = options.limit || 50;
      const offset = options.offset || 0;
      const queryParam = buildPagedQuery({
        context: context !== 'management' ? context : undefined,
        limit,
        offset,
        search: options.search,
        temp_user_id: !currentUser ? tempUserId : undefined,
      });
      const response = currentUser
        ? await authFetch(`${API_URL}/api/drafts?${queryParam}`)
        : await fetch(`${API_URL}/api/drafts?${queryParam}`, { credentials: 'include' });
      
      if (response.ok) {
        const text = await response.text();
        
        try {
          const payload = JSON.parse(text);
          const items = Array.isArray(payload) ? payload : payload.items || [];
          setDrafts(options.append ? (prev) => [...prev, ...items] : items);
          setDraftsTotal(Array.isArray(payload) ? items.length : payload.total || items.length);
          setDraftsHasMore(!Array.isArray(payload) && Boolean(payload.hasMore));
          setDraftsOffset(offset + items.length);
          setDraftsLastParams({ context, ...options, limit, offset: offset + items.length });
          setDraftsError(null);
        } catch (parseError) {
          console.error('Drafts JSON parse error:', parseError);
          setDraftsError('Invalid response from server');
        }
      } else {
        console.warn('Failed to fetch drafts:', response.status);
        setDraftsError('Failed to load drafts');
      }
    } catch (err) {
      console.error('Error fetching drafts:', err);
      setDraftsError('Failed to load drafts. Please try again later.');
    } finally {
      setDraftsLoading(false);
    }
  }, [currentUser, authFetch, tempUserId]);

  const loadMoreDrafts = useCallback(() => {
    if (!draftsHasMore || draftsLoading) return;
    const { context = 'management', ...params } = draftsLastParams;
    return fetchDrafts(context, { ...params, offset: draftsOffset, append: true });
  }, [fetchDrafts, draftsHasMore, draftsLoading, draftsLastParams, draftsOffset]);

  // NEW: Load drafts from API on component mount
  useEffect(() => {
    if (currentUser || tempUserId) {
      fetchDrafts();
      // Refresh drafts less frequently than quotes
      const refreshInterval = setInterval(fetchDrafts, 600000); // 10 minutes
      return () => clearInterval(refreshInterval);
    }
  }, [currentUser, tempUserId, fetchDrafts]);
  
  // Functions to add product(s) to the current quote
  const addProductsToQuote = (products = []) => {
    if (!Array.isArray(products) || products.length === 0) {
      return [];
    }

    const normalizedProducts = products.map((product) => {
      const id =
        product.id ||
        `item_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      const effectivePrice = getEffectivePrice(product);
      const baselinePrice =
        product.calculatedPrice ??
        product.priceBeforeOverride ??
        (product.overridePrice != null ? product.price : undefined);

      return {
        ...product,
        id,
        notes: product.notes || '',
        price: effectivePrice,
        ...(baselinePrice != null && { calculatedPrice: baselinePrice })
      };
    });

    const additionalTotal = computeItemsTotal(normalizedProducts);

    setCurrentQuote((prev) => ({
      ...prev,
      items: [...prev.items, ...normalizedProducts],
      totalPrice: prev.totalPrice + additionalTotal,
      // Preserve convertedFromDraft flag if it exists
      ...(prev.convertedFromDraft && { convertedFromDraft: true })
    }));

    return normalizedProducts.map((product) => product.id);
  };

  const addProductToQuote = (product) => {
    const [id] = addProductsToQuote([product]);
    return id;
  };
  
  // Function to remove a product from the current quote
  const removeProductFromQuote = (productId) => {
    setCurrentQuote(prev => {
      const productToRemove = prev.items.find(item => item.id === productId);
      const productPrice = productToRemove ? getEffectivePrice(productToRemove) : 0;

      return {
        ...prev,
        items: prev.items.filter(item => item.id !== productId),
        totalPrice: Math.max(0, prev.totalPrice - productPrice)
      };
    });
  };

  // Function to update a product in the current quote
  const updateProductInQuote = (productId, updates) => {
    setCurrentQuote(prev => {
      const updatedItems = prev.items.map(item => {
        if (item.id !== productId) return item;

        const shouldCaptureBaseline =
          updates.overridePrice !== undefined &&
          item.calculatedPrice === undefined &&
          item.priceBeforeOverride === undefined;

        const merged = {
          ...item,
          ...updates,
          ...(shouldCaptureBaseline ? { priceBeforeOverride: item.price } : {})
        };

        const effective = getEffectivePrice(merged);
        return { ...merged, price: effective };
      });

      return {
        ...prev,
        items: updatedItems,
        totalPrice: computeItemsTotal(updatedItems),
        ...(prev.convertedFromDraft && { convertedFromDraft: true })
      };
    });
  };
  
  // Function to clear the current quote
  const clearCurrentQuote = () => {
    setCurrentQuote({
      items: [],
      totalPrice: 0
    });
  };

  // Function to mark current quote as converted from draft
  const markQuoteAsConvertedFromDraft = () => {
    setCurrentQuote(prev => ({
      ...prev,
      convertedFromDraft: true
    }));
  };
  
  const addQuote = async (quote) => {
    try {
      const itemsToSave = (quote.items || []).map((item) => {
        const effective = getEffectivePrice(item);
        const baselinePrice =
          item.calculatedPrice ??
          item.priceBeforeOverride ??
          (item.overridePrice != null ? item.price : undefined);

        return {
          ...item,
          price: effective,
          ...(baselinePrice != null && { calculatedPrice: baselinePrice }),
          breakdown: item.breakdown,
          notes: item.notes || ""
        };
      });
      if (itemsToSave.length === 0) {
        console.error("Attempted to save quote with no items.");
        return { success: false, error: "No items to save" };
      }

      const totalProductsPrice =
        typeof quote.totalProductsPrice === 'number'
          ? quote.totalProductsPrice
          : computeItemsTotal(itemsToSave);
      const totalPrice =
        typeof quote.totalPrice === 'number'
          ? quote.totalPrice
          : totalProductsPrice;
  
      const quoteToSubmit = {
        ...quote,
        ...(currentUser
          ? {
              // Only set printlogic_customer_id from user if not already explicitly set
              printlogic_customer_id: quote.printlogic_customer_id || currentUser.customer_id || '',
              // Only set printlogic_account_name from user if they have a linked Printlogic account
              printlogic_account_name: quote.printlogic_account_name || 
                                       (currentUser.customer_id ? currentUser.customer_name : ''),
              customerName: quote.customerName || currentUser.customer_name || '', // Preserve quote's customerName if set (from Printlogic), else use user's
              clientName: quote.clientName || '' // Actual client name
            }
          : { temp_user_id: tempUserId }),
        isMultiProduct: itemsToSave.length > 1,
        price: totalPrice,
        totalProductsPrice,
        totalPrice,
        dateRequired: quote.dateRequired || null,
        items: itemsToSave,
      };

      console.log(`Overall Quote Notes: "${quoteToSubmit.notes}"`);
      quoteToSubmit.items.forEach((item, index) => {
        console.log(`Item ${index+1} Notes: "${item.notes}"`);
      });
            
      if (currentUser && currentUser.customer_id) {
        console.log(`Including Printlogic customer info: ID=${currentUser.customer_id}, Name=${currentUser.customer_name}`);
      }
      
      console.log("Saving quote:", quoteToSubmit);
      
      const response = await fetch(`${API_URL}/api/quotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(quoteToSubmit),
      });
      
      console.log("API Response status:", response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("API Error response:", errorText);
        return { success: false, error: `Failed to save quote: ${response.status} ${errorText}` };
      }
      
      const result = await response.json();
      console.log("API Success response:", result);
      
      quoteToSubmit.id = result.quote_id;
      quoteToSubmit.status = 'pending';
      quoteToSubmit.createdAt = new Date().toISOString();

      if (currentUser) {
        quoteToSubmit.username = currentUser.username;
      } 
      
      setQuotes([quoteToSubmit, ...quotes]);
      console.log("Updated quotes state:", [quoteToSubmit, ...quotes]);
      
      clearCurrentQuote();
      
      return { success: true, quote: quoteToSubmit };
    } catch (err) {
      console.error('Error adding quote:', err);
      return { success: false, error: err.message || 'An unexpected error occurred' };
    }
  };

  // NEW: Function to add draft
  const addDraft = async (draftData) => {
    try {
      console.log('Saving draft:', draftData);

      const itemsWithEffective = Array.isArray(draftData.items)
        ? draftData.items.map((item) => {
            const effective = getEffectivePrice(item);
            const baselinePrice =
              item.calculatedPrice ??
              item.priceBeforeOverride ??
              (item.overridePrice != null ? item.price : undefined);
            return {
              ...item,
              price: effective,
              ...(baselinePrice != null && { calculatedPrice: baselinePrice })
            };
          })
        : [];

      const draftToSubmit = {
        ...draftData,
        ...(itemsWithEffective.length ? { items: itemsWithEffective } : {}),
        ...(currentUser
          ? {
              // Only set printlogic_customer_id from user if not already explicitly set
              printlogic_customer_id: draftData.printlogic_customer_id || currentUser.customer_id || '',
              customerName: currentUser.customer_name || '', // User's company name
              clientName: draftData.clientName || '' // Actual client name
            }
          : { temp_user_id: tempUserId }),
        status: 'draft',
        // Ensure we have required fields
        draftName: draftData.draftName || draftData.jobName || `Draft ${new Date().toLocaleDateString()}`,
        price: (() => {
          const items = itemsWithEffective || [];
          if (items.length > 0) return computeItemsTotal(items);
          return draftData.totalPrice || draftData.price || 0;
        })(),
        totalProductsPrice: draftData.totalProductsPrice || computeItemsTotal(itemsWithEffective || []),
        totalPrice: (() => {
          if (draftData.totalPrice != null) return draftData.totalPrice;
          if (draftData.totalProductsPrice != null) return draftData.totalProductsPrice;
          if (Array.isArray(itemsWithEffective)) return computeItemsTotal(itemsWithEffective);
          return draftData.price || 0;
        })(),
      };

      const response = await fetch(`${API_URL}/api/drafts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(draftToSubmit),
      });
      
      console.log("Draft API Response status:", response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Draft API Error response:", errorText);
        throw new Error(`Failed to save draft: ${response.status} ${errorText}`);
      }
      
      const result = await response.json();
      console.log("Draft API Success response:", result);
      
      // Add to local state
      const newDraft = {
        ...draftToSubmit,
        id: result.draft_id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      setDrafts(prev => [newDraft, ...prev]);
      console.log("Updated drafts state");
      
      return newDraft;
    } catch (err) {
      console.error('Error adding draft:', err);
      return null;
    }
  };

  // NEW: Function to update draft
  const updateDraft = async (draftId, draftData) => {
    try {
      const {
        id: _ignoredId,
        createdAt: _ignoredCreatedAt,
        created_at: _ignoredCreatedAtLegacy,
        ...draftUpdateData
      } = draftData;

      console.log('Updating draft:', draftId, draftUpdateData);
      
      const response = await fetch(`${API_URL}/api/drafts/${draftId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          ...draftUpdateData,
          updatedAt: new Date().toISOString()
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update draft: ${response.status} ${errorText}`);
      }
      
      const result = await response.json();
      
      // Update local state
      setDrafts(prev => prev.map(draft => 
        draft.id === draftId ? { ...draft, ...draftUpdateData, updatedAt: new Date().toISOString() } : draft
      ));
      
      return { success: true };
    } catch (err) {
      console.error('Error updating draft:', err);
      return { success: false, message: err.message };
    }
  };

  // NEW: Function to delete draft
  const deleteDraft = async (draftId) => {
    try {
      console.log(`Attempting to delete draft with ID: ${draftId}`);
      
      const response = await fetch(`${API_URL}/api/drafts/${draftId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      console.log(`DELETE /api/drafts/${draftId} response status: ${response.status}`);

      if (!response.ok) {
        let errorMsg = `Failed to delete draft (Status: ${response.status})`;
        try {
          const errorData = await response.json();
          errorMsg = errorData.error || errorMsg;
        } catch (e) {
          // Ignore if response is not JSON
        }
        console.error('Backend draft deletion failed:', errorMsg);
        throw new Error(errorMsg);
      }

      // Remove from local state
      setDrafts(prev => prev.filter(draft => draft.id !== draftId));
      console.log(`Successfully deleted draft ${draftId} from local state.`);
      
      return true;
    } catch (err) {
      console.error('Error deleting draft:', err);
      setDraftsError(`Failed to delete draft: ${err.message}`);
      return false;
    }
  };

  // Update quote status
  const updateQuoteStatus = async (id, status, notes = '') => {
    try {
      const response = await fetch(`${API_URL}/api/quotes/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ 
          status, 
          managerNotes: notes,
          updatedAt: new Date().toISOString() 
        }),
      });
      
      if (!response.ok) throw new Error('Failed to update quote');
      const result = await response.json();
      const updatedQuote = result.quote;
      setQuotes(prevQuotes => prevQuotes.map(quote =>
        quote.id === id ? updatedQuote : quote
      ));
      return { success: true };
    } catch (err) {
      console.error('Error updating quote status:', err);
    }
  };

  // Adjust quote pricing
  const adjustQuote = async (id, newPrice, notes = '') => {
    try {
      const quoteToAdjust = quotes.find(q => q.id === id);
      if (!quoteToAdjust) throw new Error('Quote not found');
      
      const response = await fetch(`${API_URL}/api/quotes/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ 
          status: 'adjusted',
          originalPrice: quoteToAdjust.price,
          price: newPrice,
          totalPrice: newPrice,
          managerNotes: notes,
          updatedAt: new Date().toISOString() 
        }),
      });
      
      if (!response.ok) throw new Error('Failed to adjust quote');
      const result = await response.json();
      const updatedQuote = result.quote;
      setQuotes(prevQuotes => prevQuotes.map(quote =>
          quote.id === id ? updatedQuote : quote
        ));
      return { success: true };
    } catch (err) {
      console.error('Error adjusting quote:', err);
      setQuotes(quotes.map(quote => 
        quote.id === id 
          ? { 
              ...quote, 
              status: 'adjusted',
              originalPrice: quote.price,
              price: newPrice,
              totalPrice: newPrice,
              managerNotes: notes || quote.managerNotes,
              updatedAt: new Date().toISOString() 
            } 
          : quote
      ));
    }
  };

  // Approve adjusted quote
  const approveAdjustedQuote = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/quotes/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          status: 'approved',
          updatedAt: new Date().toISOString()
        }),
      });

      if (!response.ok) throw new Error('Failed to approve quote');

      setQuotes(quotes.map(quote =>
        quote.id === id
          ? {
              ...quote,
              status: 'approved',
              updatedAt: new Date().toISOString()
            }
          : quote
      ));
    } catch (err) {
      console.error('Error approving quote:', err);
    }
  };

  // Update Printlogic customer information for a quote
  const updateQuotePrintlogicCustomer = async (id, customerId, customerName, clientName = null) => {
    try {
      const updateBody = {
        printlogic_customer_id: customerId,
        printlogic_account_name: customerName,
        customerName: customerName,
        updatedAt: new Date().toISOString()
      };

      // Include clientName if provided
      if (clientName !== null) {
        updateBody.clientName = clientName;
      }

      const response = await fetch(`${API_URL}/api/quotes/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(updateBody),
      });

      if (!response.ok) throw new Error('Failed to update Printlogic customer');
      const result = await response.json();
      const updatedQuote = result.quote;

      setQuotes(prevQuotes => prevQuotes.map(quote =>
        quote.id === id ? updatedQuote : quote
      ));

      return { success: true, message: 'Customer assigned successfully' };
    } catch (err) {
      console.error('Error updating Printlogic customer:', err);
      return { success: false, message: err.message };
    }
  };

  // Delete quote
  const deleteQuote = async (id) => {
    if (!window.confirm("Are you sure you want to permanently delete this quote?")) {
      return;
    }
    
    try {
      console.log(`Attempting to delete quote with ID: ${id}`);
      const response = await fetch(`${API_URL}/api/quotes/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      console.log(`DELETE /api/quotes/${id} response status: ${response.status}`);

      if (!response.ok) {
        let errorMsg = `Failed to delete quote (Status: ${response.status})`;
        try {
          const errorData = await response.json();
          errorMsg = errorData.error || errorMsg;
        } catch (e) {
          // Ignore if response is not JSON
        }
        console.error('Backend deletion failed:', errorMsg);
        throw new Error(errorMsg);
      }

      setQuotes(prevQuotes => prevQuotes.filter(quote => quote.id !== id));
      console.log(`Successfully deleted quote ${id} from local state.`);
      return { success: true, message: "Quote deleted successfully." };

    } catch (err) {
      console.error('Error in deleteQuote function:', err);
      setError(`Failed to delete quote: ${err.message}`);
      return { success: false, message: `Failed to delete quote: ${err.message}` };
    }
  };

  // Submit to print management system
  const submitToPrintSystem = async (id, orderStatus = 'In Progress') => {
    // Prevent duplicate submissions for the same quote
    if (pendingSubmissions.current.has(id)) {
      console.warn(`Duplicate submission attempt blocked for quote ${id}`);
      return {
        success: false,
        message: 'A submission is already in progress for this quote. Please wait.',
        submission_in_progress: true
      };
    }

    pendingSubmissions.current.add(id);

    try {
      const response = await fetch(`${API_URL}/api/quotes/${id}/submit-to-printlogic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ printlogic_order_status: orderStatus }),
      });

      const result = await response.json();

      if (!response.ok) {
        console.error('Printlogic submission failed (API):', result.message || response.statusText);
        if (result.quote) {
           setQuotes(prevQuotes => prevQuotes.map(quote =>
              quote.id === id ? result.quote : quote
           ));
        }
        return { success: false, message: result.message || 'Failed to submit to print system' };
      }

      const updatedQuote = result.quote;
      setQuotes(prevQuotes => prevQuotes.map(quote =>
        quote.id === id ? updatedQuote : quote
      ));

      return { success: true, message: result.message, orderId: result.order_number };

    } catch (err) {
      console.error('Error submitting to print system:', err);
      return { success: false, message: err.message || 'An unexpected error occurred' };
    } finally {
      // Always remove from pending set when done (success or failure)
      pendingSubmissions.current.delete(id);
    }
  };

  // Combined approve and submit to print system
  const approveAndSubmitToPrintSystem = async (id, notes = '', orderStatus = 'In Progress') => {
    // Prevent duplicate submissions for the same quote
    if (pendingSubmissions.current.has(id)) {
      console.warn(`Duplicate submission attempt blocked for quote ${id}`);
      return {
        success: false,
        message: 'A submission is already in progress for this quote. Please wait.',
        submission_in_progress: true
      };
    }

    pendingSubmissions.current.add(id);

    try {
      // Step 1: Approve the quote
      console.log(`Approving quote ${id}...`);
      const approvalResponse = await fetch(`${API_URL}/api/quotes/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ 
          status: 'approved', 
          managerNotes: notes,
          updatedAt: new Date().toISOString() 
        }),
      });
      
      if (!approvalResponse.ok) {
        const errorText = await approvalResponse.text();
        throw new Error(`Failed to approve quote: ${errorText}`);
      }
      
      const approvalResult = await approvalResponse.json();
      const approvedQuote = approvalResult.quote;
      
      // Update local state with approved quote
      setQuotes(prevQuotes => prevQuotes.map(quote =>
        quote.id === id ? approvedQuote : quote
      ));

      console.log(`Quote ${id} approved successfully, now submitting to PrintLogic...`);

      // Step 2: Submit to PrintLogic
      const submissionResponse = await fetch(`${API_URL}/api/quotes/${id}/submit-to-printlogic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ printlogic_order_status: orderStatus }),
      });

      const submissionResult = await submissionResponse.json();

      if (!submissionResponse.ok) {
        console.error('PrintLogic submission failed after approval:', submissionResult.message || submissionResponse.statusText);
        // Update state with any quote changes from the failed submission
        if (submissionResult.quote) {
           setQuotes(prevQuotes => prevQuotes.map(quote =>
              quote.id === id ? submissionResult.quote : quote
           ));
        }
        return { 
          success: false, 
          message: `Quote approved but PrintLogic submission failed: ${submissionResult.message || 'Unknown error'}`,
          approvalSucceeded: true,
          submissionSucceeded: false
        };
      }

      const finalQuote = submissionResult.quote;
      setQuotes(prevQuotes => prevQuotes.map(quote =>
        quote.id === id ? finalQuote : quote
      ));

      return {
        success: true,
        message: `Quote approved and submitted to PrintLogic successfully! ${submissionResult.message || ''}`,
        orderId: submissionResult.order_number,
        approvalSucceeded: true,
        submissionSucceeded: true
      };

    } catch (err) {
      console.error('Error in combined approve and submit:', err);
      return {
        success: false,
        message: err.message || 'An unexpected error occurred during approve and submit',
        approvalSucceeded: false,
        submissionSucceeded: false
      };
    } finally {
      // Always remove from pending set when done (success or failure)
      pendingSubmissions.current.delete(id);
    }
  };



  // Get quotes by status
  const getQuotesByStatus = (status) => {
    return quotes.filter(quote => quote.status === status);
  };

  // Make context value
  const value = {
    // Quotes
    quotes,
    quotesTotal,
    quotesHasMore,
    isLoading,
    error,
    currentQuote,
    addQuote,
    updateQuoteStatus,
    adjustQuote,
    approveAdjustedQuote,
    updateQuotePrintlogicCustomer,
    deleteQuote,
    submitToPrintSystem,
    approveAndSubmitToPrintSystem,
    getQuotesByStatus,
    
    // NEW: Drafts
    drafts,
    draftsTotal,
    draftsHasMore,
    draftsLoading,
    draftsError,
    addDraft,
    updateDraft,
    deleteDraft,
    
    // Shared utilities
    tempUserId,
    getEffectivePrice,
    addProductToQuote,
    addProductsToQuote,
    removeProductFromQuote,
    updateProductInQuote,
    clearCurrentQuote,
    markQuoteAsConvertedFromDraft,
    isAuthenticated: !!currentUser,
    userRole: currentUser?.role || 'guest',
    username: currentUser?.username,
    syncPrintlogicStatus,
    manualSyncPrintlogicStatus,
    
    // Context-specific quote and draft fetching
    fetchQuotesWithContext: fetchQuotes,
    fetchDraftsWithContext: fetchDrafts,
    loadMoreQuotes,
    loadMoreDrafts,
    
    // Quote loading functions
    quoteToLoad,
    targetCalculator,
    prepareQuoteForEditing,
    prepareDraftForQuoteReview,
    clearLoadedQuote,
    pendingUploadQuoteId,
    requestUploadForQuote,
    clearPendingUploadRequest,

    // Draft editing helpers
    editingDraftId,
    startEditingDraft,
    stopEditingDraft,
  };

  return (
    <QuoteContext.Provider value={value}>
      {children}
    </QuoteContext.Provider>
  );
};

// Create custom hook for using quote context
export const useQuotes = () => {
  const context = useContext(QuoteContext);
  if (context === undefined) {
    throw new Error('useQuotes must be used within a QuoteProvider');
  }
  return context;
};

export default QuoteContext;
